import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Card } from './ui/card';

const TermsOfService = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white p-8">
        <div className="flex items-center mb-6">
          <button 
            onClick={onClose}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Website
          </button>
        </div>

        <div className="prose max-w-none">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Agreement to Terms</h2>
              <p className="text-gray-600 mb-4">
                By accessing and using the services of Mr. Manish Pacher, Clinical Psychologist & Family Therapist, 
                you agree to be bound by these Terms of Service. If you do not agree to these terms, 
                please do not use our services or website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Professional Services</h2>
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-700">Services Offered</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Individual counseling and therapy</li>
                  <li>Family therapy sessions</li>
                  <li>Couples counseling</li>
                  <li>Teen and adolescent counseling</li>
                  <li>Clinical and aptitude testing</li>
                  <li>De-addiction programs</li>
                  <li>Online and in-person consultations</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Appointment Scheduling & Cancellation</h2>
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-700">Scheduling Policy</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Appointments must be scheduled in advance</li>
                  <li>In-person sessions: Monday-Saturday 7:00 PM - 9:00 PM, Sunday 2:00 PM - 9:00 PM</li>
                  <li>Online sessions available with flexible timing</li>
                  <li>First consultation (30 minutes) is offered free online</li>
                </ul>
                
                <h3 className="text-lg font-medium text-gray-700">Cancellation Policy</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>24-hour advance notice required for cancellations</li>
                  <li>Late cancellations or no-shows may incur charges</li>
                  <li>Emergency cancellations will be considered case-by-case</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Payment Terms</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Payment is due at the time of service unless otherwise arranged</li>
                <li>We accept cash, digital payments, and insurance (where applicable)</li>
                <li>Fees are discussed during initial consultation</li>
                <li>Returned payment charges may apply for insufficient funds</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Professional Boundaries & Ethics</h2>
              <div className="bg-blue-50 p-6 rounded-lg mb-4">
                <p className="text-gray-700">
                  <strong>Professional Standards:</strong> Our practice adheres to the highest ethical standards 
                  of psychological care and maintains professional boundaries at all times.
                </p>
              </div>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Therapeutic relationships are professional in nature</li>
                <li>Dual relationships outside therapy are avoided</li>
                <li>Gifts and social contact outside sessions are not appropriate</li>
                <li>Physical contact is limited to therapeutic necessity only</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Confidentiality & Privacy</h2>
              <p className="text-gray-600 mb-4">
                Client confidentiality is paramount to our practice. Information shared during sessions is protected, 
                except in specific circumstances:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Imminent danger to self or others</li>
                <li>Suspected child or elder abuse (legal requirement)</li>
                <li>Court-ordered disclosures</li>
                <li>Written client consent for specific purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Online Services & Technology</h2>
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-700">Online Session Guidelines</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Secure, private internet connection required</li>
                  <li>Quiet, private space necessary for sessions</li>
                  <li>Technical issues may require session rescheduling</li>
                  <li>Emergency procedures different for online vs. in-person sessions</li>
                </ul>
                
                <h3 className="text-lg font-medium text-gray-700">Website Usage</h3>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Information provided is for educational purposes</li>
                  <li>Website content does not replace professional consultation</li>
                  <li>Contact forms are secure but not for crisis situations</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Limitations & Disclaimers</h2>
              <div className="bg-yellow-50 p-6 rounded-lg mb-4">
                <p className="text-gray-700">
                  <strong>Important:</strong> Psychological services carry no guarantee of specific outcomes. 
                  Results vary based on individual circumstances, commitment, and other factors.
                </p>
              </div>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Treatment outcomes cannot be guaranteed</li>
                <li>Client participation and honesty are essential for progress</li>
                <li>Emergency services are not provided through this website</li>
                <li>In crisis situations, contact emergency services immediately</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Termination of Services</h2>
              <p className="text-gray-600 mb-4">
                Either party may terminate the therapeutic relationship:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Client may discontinue services at any time</li>
                <li>Therapist may terminate if therapeutic goals are not being met</li>
                <li>Referrals to other professionals may be provided when appropriate</li>
                <li>Records will be maintained according to professional standards</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Emergency Situations</h2>
              <div className="bg-red-50 p-6 rounded-lg mb-4 border-l-4 border-red-400">
                <p className="text-red-800 font-semibold">
                  FOR MENTAL HEALTH EMERGENCIES:
                </p>
                <p className="text-red-700 mt-2">
                  Call emergency services (100/112) or go to your nearest emergency room immediately. 
                  This website and contact forms are not monitored 24/7 and should not be used for urgent situations.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Contact Information</h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-2">
                  <strong>For questions about these terms:</strong>
                </p>
                <p className="text-gray-600">
                  Mr. Manish Pacher<br/>
                  Clinical Psychologist & Family Therapist<br/>
                  Phone: +91 9167250014<br/>
                  Email: manish.pacher@gmail.com<br/>
                  Locations: Lokhandwala Complex, Andheri West & Andheri East
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Changes to Terms</h2>
              <p className="text-gray-600">
                These terms may be updated periodically to reflect changes in our practice or legal requirements. 
                Clients will be notified of significant changes. Continued use of our services constitutes 
                acceptance of updated terms.
              </p>
              <p className="text-sm text-gray-500 mt-4">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </section>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TermsOfService;